import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';

function getLocalDateStr(d = new Date()) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}


const BUILDINGS = {
  '1幢': { floors: 32, units: ['01','02','03','04'], unitGroups: [
    { name: '一单元', rooms: ['01','02'] }, { name: '二单元', rooms: ['03','04'] }
  ] },
  '2幢': { floors: 32, units: ['01','02'], unitGroups: [
    { name: '一单元', rooms: ['01','02'] }
  ] },
  '3幢': { floors: 32, units: ['01','02'], unitGroups: [
    { name: '一单元', rooms: ['01','02'] }
  ] },
  '5幢': { floors: 32, units: ['01','02','03','04'], unitGroups: [
    { name: '一单元', rooms: ['01','02'] }, { name: '二单元', rooms: ['03','04'] }
  ] },
  '6幢': { floors: 32, units: ['01','02','03','04'], unitGroups: [
    { name: '一单元', rooms: ['01','02'] }, { name: '二单元', rooms: ['03','04'] }
  ] },
};

const PUBLIC_METERS = ['幼儿园','绿化','消防','业主总表','小区总表'];

const STATUS_COLORS = { done: '#52c41a', none: '#d9d9d9' };

export default function WaterMeter({ user }) {
  const navigate = useNavigate();
  const [page, setPage] = useState('home'); // home | building | unit | report
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [selectedEntranceUnit, setSelectedEntranceUnit] = useState('一单元');
  const [selectedFloor, setSelectedFloor] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [selectedPublic, setSelectedPublic] = useState('');
  const [meters, setMeters] = useState([]);
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ reading: '', note: '' });
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [currentMeter, setCurrentMeter] = useState(null);
  const [lastReading, setLastReading] = useState(null);
  const [report, setReport] = useState(null);
  const [printData, setPrintData] = useState(null);
  const [viewImage, setViewImage] = useState(null);
  const [baselineStatus, setBaselineStatus] = useState({
    date: '2026-06-08',
    total: 0,
    completed: 0,
    missing: []
  });
  const [replacementRecords, setReplacementRecords] = useState([]);

  // ===== WATER ALERT HOME V2 =====
  const getDefaultAlertMonth = () => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0');
  };

  const [waterAlertMonth, setWaterAlertMonth] =
    useState(getDefaultAlertMonth());

  const [waterAlertData, setWaterAlertData] = useState({
    summary: null,
    alerts: []
  });

  const [waterAlertLoading, setWaterAlertLoading] =
    useState(false);

  const [waterAlertError, setWaterAlertError] =
    useState('');
  // ===== END WATER ALERT HOME V2 STATE =====

  const canRead = ['admin','manager','general_manager','steward_chief','steward','engineer','security'].includes(user.role);
  const canEditWaterReading = ['admin','general_manager','manager','steward_chief','steward'].includes(user?.role);
  const canRegisterReplacement = [
    'admin','general_manager','manager','steward_chief','steward'
  ].includes(user?.role);

  const canViewWaterAlerts = [
    'admin',
    'general_manager',
    'manager',
    'steward_chief',
    'steward',
    'engineer',
    'finance'
  ].includes(user?.role);

  const fetchWaterAlerts = async (
    month = waterAlertMonth,
    showError = false
  ) => {
    setWaterAlertLoading(true);
    setWaterAlertError('');

    try {
      const res = await API.get('/water/alerts', {
        params: { month }
      });

      setWaterAlertData({
        summary: res.data?.summary || null,
        alerts: Array.isArray(res.data?.alerts)
          ? res.data.alerts
          : []
      });

      setWaterAlertMonth(month);
    } catch (err) {
      console.error('读取水表预警失败', err);

      const msg =
        err.response?.data?.message ||
        '读取水表异常预警失败';

      setWaterAlertError(msg);

      if (showError) alert(msg);
    } finally {
      setWaterAlertLoading(false);
    }
  };

  useEffect(() => {
    fetchMeters();

    if (canViewWaterAlerts) {
      fetchWaterAlerts(getDefaultAlertMonth(), false);
      fetchBaselineStatus();
    }
  }, []);

  const fetchBaselineStatus = async () => {
    try {
      const res = await API.get('/water/baseline-status', {
        params: { date: '2026-06-08' }
      });
      setBaselineStatus(res.data);
    } catch (err) {
      console.error('读取历史底数状态失败', err);
    }
  };

  const fetchReplacementRecords = async meterId => {
    try {
      const res = await API.get('/water/replacements', {
        params: { meter_id: meterId }
      });
      setReplacementRecords(res.data || []);
    } catch (err) {
      console.error('读取换表记录失败',err);
      setReplacementRecords([]);
    }
  };

  const registerReplacement = async () => {
    if (!currentMeter || !canRegisterReplacement) return;

    const replacementDate = window.prompt(
      '请输入换表日期（YYYY-MM-DD）：',
      getLocalDateStr()
    );
    if (replacementDate === null) return;

    if (!/^\d{4}-\d{2}-\d{2}$/.test(replacementDate)) {
      alert('日期格式不正确');
      return;
    }

    const oldMeterNo = window.prompt(
      '旧水表编号（没有可留空）：',
      currentMeter.meter_no || ''
    );
    if (oldMeterNo === null) return;

    const oldFinal = window.prompt(
      '请输入旧表拆除时的最终读数：',
      ''
    );
    if (oldFinal === null) return;

    const oldFinalReading = Number(oldFinal);
    if (!Number.isFinite(oldFinalReading) ||
        oldFinalReading < 0) {
      alert('旧表拆除读数不正确');
      return;
    }

    const newMeterNo = window.prompt(
      '请输入新水表编号（没有可留空）：',
      ''
    );
    if (newMeterNo === null) return;

    const newInitial = window.prompt(
      '请输入新表安装时的起始读数：',
      '0'
    );
    if (newInitial === null) return;

    const newInitialReading = Number(newInitial);
    if (!Number.isFinite(newInitialReading) ||
        newInitialReading < 0) {
      alert('新表安装起数不正确');
      return;
    }

    const reason = window.prompt(
      '请输入换表原因：',
      '水表故障更换'
    );
    if (reason === null) return;

    if (!window.confirm(
      '请核对换表信息：\n' +
      '日期：' + replacementDate + '\n' +
      '旧表末数：' + oldFinalReading + '吨\n' +
      '新表起数：' + newInitialReading + '吨\n\n' +
      '确认登记吗？'
    )) return;

    try {
      await API.post('/water/replacements',{
        meter_id: currentMeter.id,
        replacement_date: replacementDate,
        old_meter_no: oldMeterNo.trim(),
        old_final_reading: oldFinalReading,
        new_meter_no: newMeterNo.trim(),
        new_initial_reading: newInitialReading,
        reason: reason.trim()
      });

      alert('换表登记成功，系统将自动分段计算用量');
      await fetchReplacementRecords(currentMeter.id);
      await fetchMeters();
      await fetchWaterAlerts(waterAlertMonth,false);
    } catch (err) {
      alert(err.response?.data?.message || '换表登记失败');
    }
  };

  const openAlertMeter = async alertItem => {
    const meter = meters.find(m => Number(m.id) === Number(alertItem.meter_id));

    if (!meter) {
      alert('没有找到该户水表档案');
      return;
    }

    const uiUnit =
      meter.unit === '2单元' ? '二单元' : '一单元';

    setCurrentMeter(meter);
    setSelectedBuilding(meter.building);
    setSelectedEntranceUnit(uiUnit);
    setSelectedFloor(String(meter.floor));
    setSelectedUnit(String(meter.room).padStart(2, '0'));
    setSelectedPublic('');

    await Promise.all([
      fetchReadings(meter.id),
      fetchReplacementRecords(meter.id)
    ]);
    setPage('unit');
  };

  const editAlertReading = async (alertItem, target) => {
    if (!canEditWaterReading) return;

    const targetDate =
      target === 'start'
        ? alertItem.current_start_date
        : alertItem.current_end_date;

    const oldReading =
      target === 'start'
        ? alertItem.current_start_reading
        : alertItem.current_end_reading;

    if (!targetDate) {
      alert('这条异常没有可修改的对应抄表日期');
      return;
    }

    try {
      const result = await API.get('/water/readings', {
        params: { meter_id: alertItem.meter_id }
      });

      const record = result.data.find(r =>
        String(r.read_date).slice(0,10) ===
        String(targetDate).slice(0,10)
      );

      if (!record) {
        alert('没有找到对应的原始抄表记录');
        return;
      }

      const input = window.prompt(
        '修改' +
        String(targetDate).slice(0,10) +
        '的水表读数：',
        String(oldReading ?? record.reading)
      );

      if (input === null) return;

      const newReading = Number(input);

      if (!Number.isFinite(newReading) || newReading < 0) {
        alert('请输入正确的水表数字');
        return;
      }

      const reason = window.prompt(
        '请输入修改原因，例如：抄表录入错误、OCR识别错误、水表更换：',
        ''
      );

      if (reason === null) return;
      if (!reason.trim()) {
        alert('必须填写修改原因');
        return;
      }

      if (!window.confirm(
        '确认将读数由 ' + oldReading +
        ' 修改为 ' + newReading + ' 吗？'
      )) return;

      await API.put('/water/readings/' + record.id, {
        reading: newReading,
        reason: reason.trim()
      });

      alert('修改成功，异常数据已重新计算');
      await fetchWaterAlerts(waterAlertMonth, true);
      await fetchBaselineStatus();
    } catch (err) {
      alert(err.response?.data?.message || '修改失败');
    }
  };

  const addMissingBaseline = async item => {
    if (!canEditWaterReading) return;

    const label =
      item.building + item.unit +
      item.floor + '层' +
      String(item.room).padStart(2,'0') + '室';

    const input = window.prompt(
      '补录 ' + label + ' 在2026年6月8日的水表底数：',
      ''
    );

    if (input === null) return;

    const reading = Number(input);

    if (!Number.isFinite(reading) || reading < 0) {
      alert('请输入正确的水表数字');
      return;
    }

    const reason = window.prompt(
      '请输入补录依据或说明：',
      '根据原始抄表资料人工核对'
    );

    if (reason === null) return;

    if (!window.confirm(
      '确认补录：\n' +
      label + '\n' +
      '日期：2026-06-08\n' +
      '读数：' + reading + '吨'
    )) return;

    try {
      await API.post('/water/readings/manual', {
        meter_id: item.meter_id,
        reading,
        read_date: '2026-06-08',
        reason: reason.trim()
      });

      alert('历史底数补录成功');
      await fetchBaselineStatus();
      await fetchWaterAlerts(waterAlertMonth, false);
    } catch (err) {
      alert(err.response?.data?.message || '补录失败');
    }
  };

  const fetchMeters = async () => {
    try {
      const res = await API.get('/water/meters');
      setMeters(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchReadings = async (meterId) => {
    try {
      const res = await API.get('/water/readings', { params: { meter_id: meterId } });
      setReadings(res.data);
      if (res.data.length > 0) setLastReading(res.data[0]);
      else setLastReading(null);
    } catch (err) { console.error(err); }
  };

  const fetchPrintData = async () => {
    setLoading(true);
    try {
      const res = await API.get('/water/report/print-data');
      setPrintData(res.data);
    } finally { setLoading(false); }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await API.get('/water/report');
      setReport(res.data);
    } finally { setLoading(false); }
  };

  // V2.8.3 WATER STRICT LOOKUP
  // 住宅水表必须按 楼幢+单元+楼层+房号 精确定位。
  // V2.8.3.1 WATER UNIT NORMALIZE
  // 页面显示“一单元/二单元”，数据库统一保存“1单元/2单元”
  const normalizeEntranceUnit = (entranceUnit) => {
    if (entranceUnit === '一单元') return '1单元';
    if (entranceUnit === '二单元') return '2单元';
    return entranceUnit;
  };

  const getMeter = (building, entranceUnit, floor, room) => {
    const dbUnit = normalizeEntranceUnit(entranceUnit);
    return meters.find(m =>
      m.meter_type === '住宅' &&
      m.building === building &&
      m.unit === dbUnit &&
      String(m.floor) === String(floor) &&
      String(m.room).padStart(2,'0') === String(room).padStart(2,'0')
    );
  };

  const getPublicMeter = (type) => {
    return meters.find(m => m.meter_type === type);
  };

  const getBuildingStats = (building) => {
    const bMeters = meters.filter(m => m.building === building && m.meter_type === '住宅');
    // 找有抄录记录的
    return { total: bMeters.length, done: 0 };
  };

  // AI识别水表度数
  const handleOCR = async (file) => {
    setOcrLoading(true);
    try {
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result.split(',')[1]);
        reader.readAsDataURL(file);
      });

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 100,
          messages: [{
            role: 'user',
            content: [
              { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: base64 } },
              { type: 'text', text: '这是水表照片，请识别水表显示的数字读数，只返回纯数字，不要其他文字。如无法识别返回空。' }
            ]
          }]
        })
      });
      const data = await response.json();
      const text = data.content?.[0]?.text?.trim() || '';
      const num = text.replace(/[^\d.]/g, '');
      if (num) setForm(f => ({ ...f, reading: num }));
    } catch (err) {
      console.error('OCR失败', err);
    } finally {
      setOcrLoading(false);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmitReading = async () => {
    if (!image) { alert('请先拍照上传水表照片'); return; }
    if (!form.reading) { alert('请输入度数'); return; }
    if (!currentMeter) { alert('水表信息错误'); return; }
    try {
      const data = new FormData();
      data.append('meter_id', currentMeter.id);
      data.append('reading', form.reading);
      data.append('read_date', getLocalDateStr());
      if (form.note) data.append('note', form.note);
      if (image) data.append('image', image);
      await API.post('/water/readings', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert('抄录成功！');
      setForm({ reading: '', note: '' });
      setImage(null); setPreview(null);
      await fetchReadings(currentMeter.id);
      setPage(selectedPublic ? 'home' : 'building');
    } catch (err) {
      alert(err.response?.data?.message || '提交失败');
    }
  };

  const openUnitPage = async (building, entranceUnit, floor, room) => {
    const meter = getMeter(building, entranceUnit, floor, room);
    if (!meter) {
      alert(`未找到水表：${building}${entranceUnit}${floor}层${room}室，请联系管理员检查水表档案`);
      return;
    }
    setCurrentMeter(meter);
    setSelectedFloor(String(floor));
    setSelectedUnit(String(room).padStart(2,'0'));
    await Promise.all([
      fetchReadings(meter.id),
      fetchReplacementRecords(meter.id)
    ]);
    setPage('unit');
  };

  const openPublicPage = async (type) => {
    let meter = getPublicMeter(type);
    setCurrentMeter(meter);
    setSelectedPublic(type);
    if (meter) await fetchReadings(meter.id);
    setPage('unit');
  };

  // 首页
  if (page === 'home') return (
    <div style={s.container}>
      <div style={s.header}>
        <h1 style={s.headerTitle}>💧 水表抄录</h1>
        <button style={s.reportBtn} onClick={() => { fetchReport(); setPage('report'); }}>📊 报表</button>
      </div>

      <div style={s.content}>
        {/* WATER ALERT HOME V2 */}
        {canViewWaterAlerts && (
          <div style={{
            background:'#fff',
            border:'1px solid #e8e8e8',
            borderRadius:'14px',
            padding:'15px',
            marginBottom:'18px',
            boxShadow:'0 2px 10px rgba(0,0,0,.06)'
          }}>
            <div style={{
              display:'flex',
              justifyContent:'space-between',
              alignItems:'center',
              gap:'10px',
              marginBottom:'12px'
            }}>
              <div>
                <div style={{
                  fontSize:'17px',
                  fontWeight:800,
                  color:'#333'
                }}>
                  ⚠️ 用户水表异常预警
                </div>

                <div style={{
                  fontSize:'12px',
                  color:'#888',
                  marginTop:'4px'
                }}>
                  {waterAlertMonth} · 上一个完整月份
                </div>
              </div>

              <button
                onClick={() =>
                  fetchWaterAlerts(waterAlertMonth, true)
                }
                style={{
                  border:0,
                  borderRadius:'8px',
                  background:'#1677ff',
                  color:'#fff',
                  padding:'8px 12px',
                  fontWeight:700
                }}
              >
                {waterAlertLoading ? '分析中...' : '刷新'}
              </button>
            </div>

            <div style={{
              display:'grid',
              gridTemplateColumns:'repeat(5,1fr)',
              gap:'5px'
            }}>
              {[
                ['⚫','数据',waterAlertData.summary?.gray || 0,'#434343','#f5f5f5'],
                ['🔴','严重',waterAlertData.summary?.red || 0,'#cf1322','#fff1f0'],
                ['🟠','警告',waterAlertData.summary?.orange || 0,'#d46b08','#fff7e6'],
                ['🟡','提醒',waterAlertData.summary?.yellow || 0,'#ad6800','#fffbe6'],
                ['🔵','低用水',waterAlertData.summary?.blue || 0,'#0958d9','#e6f4ff']
              ].map(([icon,title,num,color,bg]) => (
                <div key={title} style={{
                  background:bg,
                  borderRadius:'9px',
                  padding:'9px 2px',
                  textAlign:'center',
                  minWidth:0
                }}>
                  <div style={{
                    fontSize:'20px',
                    fontWeight:900,
                    color
                  }}>
                    {num}
                  </div>

                  <div style={{
                    fontSize:'10px',
                    color,
                    whiteSpace:'nowrap'
                  }}>
                    {icon}{title}
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              marginTop:'11px',
              paddingTop:'9px',
              borderTop:'1px solid #f0f0f0',
              display:'flex',
              flexWrap:'wrap',
              gap:'6px 14px',
              fontSize:'12px',
              color:'#666'
            }}>
              <span>
                异常：
                <b>{waterAlertData.summary?.total || 0}户</b>
              </span>

              <span>
                高用水：
                <b style={{color:'#d46b08'}}>
                  {waterAlertData.summary?.high_total || 0}户
                </b>
              </span>

              <span>
                低用水：
                <b style={{color:'#0958d9'}}>
                  {waterAlertData.summary?.low_total || 0}户
                </b>
              </span>

              <span>
                户均：
                <b style={{color:'#1677ff'}}>
                  {waterAlertData.summary?.community_average ?? '--'}吨
                </b>
              </span>
            </div>

            {waterAlertError && (
              <div style={{
                marginTop:'8px',
                color:'#cf1322',
                fontSize:'12px'
              }}>
                {waterAlertError}
              </div>
            )}

            {baselineStatus.missing?.length > 0 && (
              <div style={{
                marginTop:'12px',
                padding:'10px',
                background:'#fff7e6',
                border:'1px solid #ffd591',
                borderRadius:'10px'
              }}>
                <div style={{
                  fontSize:'14px',
                  fontWeight:800,
                  color:'#ad4e00',
                  marginBottom:'8px'
                }}>
                  📝 缺少2026年6月8日底数：
                  {baselineStatus.missing.length}户
                </div>

                {baselineStatus.missing.map(item => (
                  <div key={item.meter_id} style={{
                    display:'flex',
                    alignItems:'center',
                    justifyContent:'space-between',
                    gap:'8px',
                    padding:'7px 0',
                    borderTop:'1px solid #ffe7ba',
                    fontSize:'13px'
                  }}>
                    <span>
                      {item.building}{item.unit}
                      {item.floor}层{String(item.room).padStart(2,'0')}室
                    </span>

                    {canEditWaterReading && (
                      <button
                        onClick={() => addMissingBaseline(item)}
                        style={{
                          border:0,
                          borderRadius:'7px',
                          padding:'6px 10px',
                          background:'#fa8c16',
                          color:'#fff',
                          fontWeight:700
                        }}
                      >
                        补录底数
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {waterAlertData.alerts?.length > 0 && (
              <div style={{marginTop:'14px'}}>
                <div style={{
                  fontSize:'15px',
                  fontWeight:800,
                  color:'#333',
                  marginBottom:'8px'
                }}>
                  异常水表明细
                </div>

                {waterAlertData.alerts.map((item, index) => {
                  const color =
                    item.level === 'gray' ? '#434343' :
                    item.level === 'red' ? '#cf1322' :
                    item.level === 'orange' ? '#d46b08' :
                    item.level === 'blue' ? '#0958d9' :
                    '#ad6800';

                  const bg =
                    item.level === 'gray' ? '#fafafa' :
                    item.level === 'red' ? '#fff1f0' :
                    item.level === 'orange' ? '#fff7e6' :
                    item.level === 'blue' ? '#e6f4ff' :
                    '#fffbe6';

                  return (
                    <div key={item.meter_id + '-' + index} style={{
                      background:bg,
                      border:'1px solid ' + color,
                      borderRadius:'10px',
                      padding:'11px',
                      marginBottom:'9px'
                    }}>
                      <div style={{
                        display:'flex',
                        justifyContent:'space-between',
                        gap:'8px',
                        alignItems:'center'
                      }}>
                        <div style={{
                          fontSize:'15px',
                          fontWeight:900,
                          color:'#222'
                        }}>
                          {item.room_no ||
                            item.building + item.unit +
                            item.floor + item.room}
                        </div>

                        <span style={{
                          background:color,
                          color:'#fff',
                          borderRadius:'10px',
                          padding:'3px 8px',
                          fontSize:'11px',
                          fontWeight:800
                        }}>
                          {item.level_name} · {item.alert_name}
                        </span>
                      </div>

                      {(item.owner_name || item.occupancy_status) && (
                        <div style={{
                          marginTop:'6px',
                          color:'#555',
                          fontSize:'12px'
                        }}>
                          业主：{item.owner_name || '未登记'}
                          {item.occupancy_status
                            ? ' · ' + item.occupancy_status
                            : ''}
                        </div>
                      )}

                      <div style={{
                        marginTop:'9px',
                        display:'grid',
                        gridTemplateColumns:'1fr 1fr 1fr',
                        gap:'6px'
                      }}>
                        <div style={{
                          background:'#fff',
                          borderRadius:'7px',
                          padding:'7px',
                          textAlign:'center'
                        }}>
                          <div style={{fontSize:'10px',color:'#888'}}>
                            上期读数
                          </div>
                          <div style={{fontWeight:800,color:'#333'}}>
                            {item.current_start_reading ?? '--'}
                          </div>
                          <div style={{fontSize:'9px',color:'#999'}}>
                            {String(item.current_start_date || '').slice(0,10)}
                          </div>
                        </div>

                        <div style={{
                          background:'#fff',
                          borderRadius:'7px',
                          padding:'7px',
                          textAlign:'center'
                        }}>
                          <div style={{fontSize:'10px',color:'#888'}}>
                            本期读数
                          </div>
                          <div style={{fontWeight:800,color:'#333'}}>
                            {item.current_end_reading ?? '--'}
                          </div>
                          <div style={{fontSize:'9px',color:'#999'}}>
                            {String(item.current_end_date || '').slice(0,10)}
                          </div>
                        </div>

                        <div style={{
                          background:'#fff',
                          borderRadius:'7px',
                          padding:'7px',
                          textAlign:'center'
                        }}>
                          <div style={{fontSize:'10px',color:'#888'}}>
                            实际用量
                          </div>
                          <div style={{fontWeight:900,color}}>
                            {item.current_usage ?? '--'}吨
                          </div>
                          <div style={{fontSize:'9px',color:'#999'}}>
                            户均{item.community_average ?? '--'}吨
                          </div>
                        </div>
                      </div>

                      <div style={{
                        marginTop:'8px',
                        color,
                        fontSize:'12px',
                        lineHeight:1.5
                      }}>
                        {item.message}
                      </div>

                      {item.replacement && (
                        <div style={{
                          marginTop:'8px',
                          background:'#f0f5ff',
                          border:'1px solid #adc6ff',
                          borderRadius:'7px',
                          padding:'8px',
                          fontSize:'12px',
                          color:'#1d39c4'
                        }}>
                          🔄 已登记换表：
                          {item.replacement.replacement_date}
                          {' · '}旧表末数
                          {item.replacement.old_final_reading}吨
                          {' · '}新表起数
                          {item.replacement.new_initial_reading}吨
                          {item.replacement_adjusted
                            ? ' · 本期已按新旧表分段计算'
                            : ''}
                        </div>
                      )}

                      <div style={{
                        display:'flex',
                        flexWrap:'wrap',
                        gap:'7px',
                        marginTop:'9px'
                      }}>
                        <button
                          onClick={() => openAlertMeter(item)}
                          style={{
                            border:'1px solid #1677ff',
                            borderRadius:'7px',
                            background:'#fff',
                            color:'#1677ff',
                            padding:'6px 9px',
                            fontWeight:700
                          }}
                        >
                          查看该户记录
                        </button>

                        {canEditWaterReading &&
                          item.current_start_date && (
                          <button
                            onClick={() =>
                              editAlertReading(item, 'start')
                            }
                            style={{
                              border:'1px solid #fa8c16',
                              borderRadius:'7px',
                              background:'#fff',
                              color:'#d46b08',
                              padding:'6px 9px',
                              fontWeight:700
                            }}
                          >
                            修改上期读数
                          </button>
                        )}

                        {canEditWaterReading &&
                          item.current_end_date && (
                          <button
                            onClick={() =>
                              editAlertReading(item, 'end')
                            }
                            style={{
                              border:0,
                              borderRadius:'7px',
                              background:'#1677ff',
                              color:'#fff',
                              padding:'6px 9px',
                              fontWeight:700
                            }}
                          >
                            修改本期读数
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* 公共水表 */}
        <div style={s.sectionTitle}>公共水表</div>
        <div style={s.publicGrid}>
          {PUBLIC_METERS.map(type => {
            const m = getPublicMeter(type);
            return (
              <button key={type} style={s.publicCard} onClick={() => openPublicPage(type)}>
                <div style={s.publicIcon}>
                  {type === '幼儿园' ? '🏫' : type === '绿化' ? '🌿' : type === '消防' ? '🚒' : '💧'}
                </div>
                <div style={s.publicName}>{type}</div>
                <div style={s.publicStatus}>{m ? '点击抄录' : '点击抄录'}</div>
              </button>
            );
          })}
        </div>

        {/* 住宅楼幢 */}
        <div style={s.sectionTitle}>住宅楼幢</div>
        <div style={s.buildingGrid}>
          {Object.keys(BUILDINGS).map(b => (
            <button key={b} style={s.buildingCard} onClick={() => { setSelectedBuilding(b); setSelectedEntranceUnit('一单元'); setPage('building'); }}>
              <div style={s.buildingIcon}>🏢</div>
              <div style={s.buildingName}>{b}</div>
              <div style={s.buildingUnits}>{BUILDINGS[b].floors * BUILDINGS[b].units.length} 户</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // 楼幢页面
  if (page === 'building') {
    const bInfo = BUILDINGS[selectedBuilding];
    const activeUnitGroup = bInfo.unitGroups.find(g => g.name === selectedEntranceUnit) || bInfo.unitGroups[0];
    return (
      <div style={s.container}>
        <div style={s.header}>
          <button style={s.back} onClick={() => setPage('home')}>← 返回</button>
          <h2 style={s.headerTitle}>{selectedBuilding}</h2>
          <div style={{width:60}} />
        </div>
        <div style={s.content}>
          {bInfo.unitGroups.length > 1 && (
            <div style={{display:'flex', gap:'10px', marginBottom:'16px', position:'sticky', top:'74px', zIndex:20, background:'#f5f7fa', padding:'8px 0'}}>
              {bInfo.unitGroups.map(group => (
                <button key={group.name} onClick={() => setSelectedEntranceUnit(group.name)}
                  style={{flex:1, padding:'11px', border:'none', borderRadius:'10px', fontSize:'16px', fontWeight:700,
                    background: selectedEntranceUnit === group.name ? '#0897c2' : '#fff',
                    color: selectedEntranceUnit === group.name ? '#fff' : '#087fa8',
                    boxShadow:'0 1px 5px rgba(0,0,0,.12)'}}>
                  {group.name}
                </button>
              ))}
            </div>
          )}
          {Array.from({length: bInfo.floors}, (_, i) => bInfo.floors + 1 - i).map(floor => (
            <div key={floor} style={s.floorRow}>
              <div style={s.floorLabel}>{floor}F</div>
              <div style={s.unitRow}>
                {activeUnitGroup.rooms.map(room => {
                  const m = getMeter(selectedBuilding, selectedEntranceUnit, String(floor), room);
                  return (
                    <button key={room} style={{...s.unitBtn, background: m ? '#e6f7ff' : '#fff'}}
                      onClick={() => openUnitPage(selectedBuilding, selectedEntranceUnit, String(floor), room)}>
                      {floor}{room}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 抄录页面
  if (page === 'unit') {
    const title = selectedPublic || `${selectedBuilding} ${selectedEntranceUnit} ${selectedFloor}层 ${selectedUnit}室`;
    return (
      <div style={s.container}>
        <div style={s.header}>
          <button style={s.back} onClick={() => { setPage(selectedPublic ? 'home' : 'building'); setSelectedPublic(''); }}>← 返回</button>
          <h2 style={s.headerTitle2}>{title}</h2>
          <div style={{width:60}} />
        </div>
        <div style={s.content}>
          {/* 上次记录 */}
          <div style={s.lastCard}>
            <div style={s.lastTitle}>📋 上次抄录</div>
            {lastReading ? (
              <>
                <div style={s.lastReading}>{lastReading.reading} <span style={s.unit}>吨</span></div>
                <div style={s.lastDate}>{lastReading.read_date} · {lastReading.reader_name}</div>
              </>
            ) : <div style={s.noRecord}>暂无记录</div>}
          </div>

          {/* 换表记录 */}
          {currentMeter?.meter_type === '住宅' && (
            <div style={{
              background:'#fff',
              borderRadius:'12px',
              padding:'14px',
              marginBottom:'12px',
              boxShadow:'0 2px 6px rgba(0,119,182,.08)'
            }}>
              <div style={{
                display:'flex',
                justifyContent:'space-between',
                alignItems:'center',
                gap:'10px'
              }}>
                <div style={{
                  fontSize:'14px',
                  fontWeight:800,
                  color:'#0077b6'
                }}>
                  🔄 水表更换记录
                </div>

                {canRegisterReplacement && (
                  <button
                    onClick={registerReplacement}
                    style={{
                      border:0,
                      borderRadius:'8px',
                      background:'#fa8c16',
                      color:'#fff',
                      padding:'7px 11px',
                      fontWeight:700
                    }}
                  >
                    登记换表
                  </button>
                )}
              </div>

              {replacementRecords.length === 0 ? (
                <div style={{
                  color:'#aaa',
                  fontSize:'12px',
                  marginTop:'9px'
                }}>
                  暂无换表记录
                </div>
              ) : replacementRecords.map(record => (
                <div key={record.id} style={{
                  marginTop:'10px',
                  padding:'10px',
                  background:'#fff7e6',
                  border:'1px solid #ffd591',
                  borderRadius:'8px',
                  fontSize:'12px',
                  lineHeight:1.8
                }}>
                  <div style={{
                    fontWeight:800,
                    color:'#ad4e00'
                  }}>
                    换表日期：{record.replacement_date}
                  </div>
                  <div>
                    旧表：
                    {record.old_meter_no || '未登记编号'}
                    {' · '}拆除读数
                    {record.old_final_reading}吨
                  </div>
                  <div>
                    新表：
                    {record.new_meter_no || '未登记编号'}
                    {' · '}安装起数
                    {record.new_initial_reading}吨
                  </div>
                  {record.reason && (
                    <div>原因：{record.reason}</div>
                  )}
                  <div style={{color:'#999'}}>
                    登记人：{record.creator_name || '管理员'}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 拍照识别 */}
          {canRead && (
            <div style={s.readCard}>
              <div style={s.readTitle}>📷 本次抄录</div>
              <label style={s.photoArea}>
                {preview ? <img src={preview} style={s.previewImg} alt="" /> : (
                  <><span style={{fontSize:'40px'}}>📷</span>
                  <span style={{color:'#888', fontSize:'13px'}}>点击拍照</span></>
                )}
                <input type="file" accept="image/*" capture="environment" style={{display:'none'}} onChange={handleImageChange} />
              </label>

              <div style={s.inputRow}>
                <input style={s.readingInput} type="number" placeholder="度数（吨）"
                  value={form.reading} onChange={e => setForm({...form, reading: e.target.value})} />
                <span style={s.unitLabel}>吨</span>
              </div>

              {lastReading && form.reading && (
                <div style={s.usageTip}>
                  本期用水：<strong>{(parseFloat(form.reading) - parseFloat(lastReading.reading)).toFixed(2)}</strong> 吨
                </div>
              )}

              <input style={s.noteInput} placeholder="备注（可选）"
                value={form.note} onChange={e => setForm({...form, note: e.target.value})} />

              <div style={{display:'flex', gap:'10px'}}>
                <button style={{...s.submitBtn, flex:1, background:'#fff', color:'#999', border:'1px solid #d9d9d9'}}
                  onClick={async () => {
                    if (!lastReading) { alert('暂无可清除的记录'); return; }
                    if (!window.confirm(`确定清除最后一次抄录（${lastReading.reading}吨，${lastReading.read_date}）？此操作不可恢复。`)) return;
                    try {
                      await API.delete(`/water/readings/last/${currentMeter.id}`);
                      alert('已清除最后一次抄录');
                      fetchReadings(currentMeter.id);
                      fetchMeters();
                    } catch (err) { alert(err.response?.data?.message || '清除失败'); }
                  }}>
                  🗑️ 清除最后一次抄录
                </button>
                <button style={{...s.submitBtn, flex:2}} onClick={handleSubmitReading}>💾 保存本次抄录</button>
              </div>
            </div>
          )}

          {/* 历史记录 */}
          {readings.length > 0 && (
            <div style={s.historyCard}>
              <div style={s.historyTitle}>📜 历史记录</div>
              {readings.slice(0, 6).map((r, i) => (
                <div key={r.id} style={s.historyRow}>
                  <span style={s.historyDate}>{r.read_date}</span>
                  <span style={s.historyReading}>{r.reading} 吨</span>
                  {i < readings.length - 1 && (
                    <span style={s.historyUsage}>+{(r.reading - readings[i+1]?.reading).toFixed(2)}</span>
                  )}
                  {r.image_path && (
                    <button style={{marginLeft:'8px', fontSize:'12px', padding:'2px 8px', borderRadius:'10px', border:'1px solid #1a4a8a', background:'#fff', color:'#1a4a8a', cursor:'pointer'}}
                      onClick={() => setViewImage(r.image_path)}>
                      📷 查看图片
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {viewImage && (
          <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.85)', zIndex:999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px'}}
            onClick={() => setViewImage(null)}>
            <img src={`/uploads/${viewImage}`} style={{maxWidth:'100%', maxHeight:'85vh', borderRadius:'8px'}} alt="" onClick={e => e.stopPropagation()} />
            <button style={{position:'absolute', top:'20px', right:'20px', background:'rgba(255,255,255,0.9)', border:'none', borderRadius:'50%', width:'36px', height:'36px', fontSize:'18px', cursor:'pointer'}}
              onClick={() => setViewImage(null)}>×</button>
          </div>
        )}
      </div>
    );
  }

  // 报表页面
  if (page === 'print') return (
    <div style={{background:'#fff', minHeight:'100vh'}}>
      <div className="no-print" style={{padding:'12px 16px', display:'flex', justifyContent:'space-between', background:'#f5f5f5', position:'sticky', top:0}}>
        <button style={s.back} onClick={() => setPage('report')}>← 返回</button>
        <button style={{...s.back, background:'#1a4a8a', color:'#fff'}} onClick={() => window.print()}>🖨️ 打印 / 另存为PDF</button>
      </div>
      {loading || !printData ? <div style={s.loading}>加载中...</div> : (
        <div style={{padding:'20px'}}>
          {printData.data.map(b => (
            <div key={b.building} style={{marginBottom:'30px', pageBreakAfter:'always'}}>
              <h3 style={{textAlign:'center', marginBottom:'4px'}}>{b.building} 水表抄录表</h3>
              <div style={{textAlign:'center', fontSize:'13px', color:'#666', marginBottom:'14px'}}>
                {printData.year}年{printData.month}月
              </div>
              <table style={{width:'100%', borderCollapse:'collapse', fontSize:'13px'}}>
                <thead>
                  <tr>
                    {b.units.map((u, i) => (
                      <React.Fragment key={i}>
                        <th style={{border:'1px solid #333', padding:'6px'}}>房号</th>
                        <th style={{border:'1px solid #333', padding:'6px'}}>读数</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {b.rows.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => (
                        <React.Fragment key={ci}>
                          <td style={{border:'1px solid #333', padding:'6px', textAlign:'center'}}>{cell?.room || ''}</td>
                          <td style={{border:'1px solid #333', padding:'6px', textAlign:'center'}}>{cell?.reading ?? ''}</td>
                        </React.Fragment>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
      <style>{`@media print { .no-print { display: none !important; } }`}</style>
    </div>
  );

  if (page === 'report') return (
    <div style={s.container}>
      <div style={s.header}>
        <button style={s.back} onClick={() => setPage('home')}>← 返回</button>
        <h2 style={s.headerTitle}>📊 抄表报表</h2>
        <div style={{display:'flex', gap:'6px'}}>
          <button style={{...s.back, fontSize:'12px'}} onClick={() => { fetchPrintData(); setPage('print'); }}>🖨️ 打印</button>
          <button style={{...s.back, fontSize:'12px'}} onClick={async () => {
            try {
              const res = await API.get('/water/report/export', { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', `水表报表_${getLocalDateStr().slice(0,7)}.xlsx`);
              document.body.appendChild(link);
              link.click();
              link.remove();
            } catch (err) { alert('导出失败'); }
          }}>📥 导出</button>
        </div>
      </div>
      <div style={s.content}>
        {loading ? <div style={s.loading}>加载中...</div> : report && (
          <>
            <div style={s.reportPeriod}>{report.last_year}年{report.last_month}月 → {report.year}年{report.month}月</div>
            {/* 公共水表 */}
            <div style={s.sectionTitle}>公共水表</div>
            {report.data.filter(d => d.meter_type !== '住宅').map(d => (
              <div key={d.id} style={s.reportRow}>
                <span style={s.reportLabel}>{d.label}</span>
                <span style={s.reportLast}>{d.last_reading ?? '-'}</span>
                <span style={s.reportThis}>{d.this_reading ?? '未抄'}</span>
                <span style={{...s.reportUsage, color: d.usage ? '#1890ff' : '#ccc'}}>{d.usage ?? '-'} 吨</span>
              </div>
            ))}
            {/* 住宅水表：按楼栋分组，每幢一张表格 */}
            {(() => {
              const residential = report.data.filter(d => d.meter_type === '住宅');
              const buildings = [...new Set(residential.map(d => d.building))].sort();
              return buildings.map(building => {
                const list = residential.filter(d => d.building === building)
                  .sort((a, b) => (a.unit||'').localeCompare(b.unit||'') || (parseInt(a.floor)||0) - (parseInt(b.floor)||0) || (a.room||'').localeCompare(b.room||''));
                const doneCount = list.filter(d => d.this_reading).length;
                return (
                  <div key={building} style={{marginBottom:'18px'}}>
                    <div style={{...s.sectionTitle, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                      <span>🏢 {building}</span>
                      <span style={{fontSize:'12px', color: doneCount===list.length ? '#52c41a' : '#fa8c16', fontWeight:600}}>
                        {doneCount}/{list.length} 已抄
                      </span>
                    </div>
                    <div style={s.reportHeader}>
                      <span style={{flex:2}}>房号</span>
                      <span style={{flex:1,textAlign:'right'}}>上月</span>
                      <span style={{flex:1,textAlign:'right'}}>本月</span>
                      <span style={{flex:1,textAlign:'right'}}>用水量</span>
                    </div>
                    {list.map(d => (
                      <div key={d.id} style={{...s.reportRow, background: !d.this_reading ? '#fff8f0' : '#fff'}}>
                        <span style={{flex:2, fontSize:'12px'}}>{d.label}</span>
                        <span style={{flex:1, textAlign:'right', fontSize:'12px', color:'#888'}}>{d.last_reading ?? '-'}</span>
                        <span style={{flex:1, textAlign:'right', fontSize:'12px', color: d.this_reading ? '#1a4a8a' : '#ff4d4f'}}>{d.this_reading ?? '未抄'}</span>
                        <span style={{flex:1, textAlign:'right', fontSize:'12px', fontWeight:600, color: d.usage ? '#52c41a' : '#ccc'}}>{d.usage ?? '-'}</span>
                      </div>
                    ))}
                  </div>
                );
              });
            })()}
          </>
        )}
      </div>
    </div>
  );

  return null;
}

const s = {
  container: { minHeight:'100vh', background:'#f0f7ff', paddingBottom:'30px' },
  header: { background:'linear-gradient(90deg, #0077b6, #0096c7)', padding:'16px', display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, zIndex:100 },
  back: { background:'transparent', border:'none', color:'#fff', fontSize:'16px', cursor:'pointer' },
  headerTitle: { color:'#fff', margin:0, fontSize:'18px', fontWeight:700 },
  headerTitle2: { color:'#fff', margin:0, fontSize:'15px', fontWeight:700, textAlign:'center', flex:1 },
  reportBtn: { background:'#fff', color:'#0077b6', border:'none', borderRadius:'16px', padding:'6px 14px', fontSize:'13px', fontWeight:600, cursor:'pointer' },
  content: { padding:'12px 16px' },
  sectionTitle: { fontSize:'14px', fontWeight:700, color:'#0077b6', padding:'10px 0 6px', borderBottom:'1px solid #d0e8ff', marginBottom:'10px' },
  publicGrid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'16px' },
  publicCard: { background:'#fff', borderRadius:'12px', padding:'16px 10px', textAlign:'center', border:'1px solid #d0e8ff', cursor:'pointer', boxShadow:'0 2px 6px rgba(0,119,182,0.08)' },
  publicIcon: { fontSize:'28px', marginBottom:'6px' },
  publicName: { fontSize:'14px', fontWeight:700, color:'#0077b6', marginBottom:'4px' },
  publicStatus: { fontSize:'12px', color:'#888' },
  buildingGrid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' },
  buildingCard: { background:'#fff', borderRadius:'12px', padding:'16px 10px', textAlign:'center', border:'1px solid #d0e8ff', cursor:'pointer', boxShadow:'0 2px 6px rgba(0,119,182,0.08)' },
  buildingIcon: { fontSize:'28px', marginBottom:'6px' },
  buildingName: { fontSize:'16px', fontWeight:700, color:'#0077b6', marginBottom:'4px' },
  buildingUnits: { fontSize:'12px', color:'#888' },
  floorRow: { display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' },
  floorLabel: { width:'28px', fontSize:'12px', color:'#0077b6', fontWeight:700, flexShrink:0 },
  unitRow: { display:'flex', gap:'6px', flexWrap:'wrap' },
  unitBtn: { padding:'8px 12px', border:'1px solid #d0e8ff', borderRadius:'8px', fontSize:'13px', cursor:'pointer', fontWeight:600 },
  lastCard: { background:'#fff', borderRadius:'12px', padding:'16px', marginBottom:'12px', boxShadow:'0 2px 6px rgba(0,119,182,0.08)' },
  lastTitle: { fontSize:'14px', fontWeight:700, color:'#0077b6', marginBottom:'10px' },
  lastReading: { fontSize:'32px', fontWeight:900, color:'#0077b6' },
  unit: { fontSize:'16px', fontWeight:400, color:'#888' },
  lastDate: { fontSize:'12px', color:'#888', marginTop:'4px' },
  noRecord: { fontSize:'14px', color:'#bbb', padding:'10px 0' },
  readCard: { background:'#fff', borderRadius:'12px', padding:'16px', marginBottom:'12px', boxShadow:'0 2px 6px rgba(0,119,182,0.08)' },
  readTitle: { fontSize:'14px', fontWeight:700, color:'#0077b6', marginBottom:'10px' },
  photoArea: { display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', border:'2px dashed #d0e8ff', borderRadius:'10px', padding:'16px', marginBottom:'10px', cursor:'pointer', minHeight:'120px', background:'#f0f7ff' },
  previewImg: { width:'100%', maxHeight:'200px', objectFit:'cover', borderRadius:'8px' },
  ocrTip: { textAlign:'center', color:'#0077b6', fontSize:'13px', marginBottom:'8px', padding:'6px', background:'#e0f0ff', borderRadius:'6px' },
  inputRow: { display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' },
  readingInput: { flex:1, padding:'12px', border:'2px solid #0077b6', borderRadius:'8px', fontSize:'20px', fontWeight:700, textAlign:'center' },
  unitLabel: { fontSize:'16px', color:'#888', fontWeight:600 },
  usageTip: { background:'#e6f7ff', border:'1px solid #91d5ff', borderRadius:'8px', padding:'8px 12px', fontSize:'14px', color:'#0077b6', marginBottom:'8px', textAlign:'center' },
  noteInput: { width:'100%', padding:'10px', border:'1.5px solid #e0e0e0', borderRadius:'8px', fontSize:'14px', marginBottom:'10px', boxSizing:'border-box' },
  submitBtn: { width:'100%', padding:'14px', background:'linear-gradient(90deg, #0077b6, #0096c7)', color:'#fff', border:'none', borderRadius:'10px', fontSize:'16px', fontWeight:700, cursor:'pointer' },
  historyCard: { background:'#fff', borderRadius:'12px', padding:'16px', boxShadow:'0 2px 6px rgba(0,119,182,0.08)' },
  historyTitle: { fontSize:'14px', fontWeight:700, color:'#0077b6', marginBottom:'10px' },
  historyRow: { display:'flex', alignItems:'center', gap:'8px', padding:'6px 0', borderBottom:'1px solid #f0f7ff', fontSize:'13px' },
  historyDate: { color:'#888', flex:1 },
  historyReading: { color:'#0077b6', fontWeight:700 },
  historyUsage: { color:'#52c41a', fontSize:'12px' },
  loading: { textAlign:'center', padding:'40px', color:'#888' },
  reportPeriod: { textAlign:'center', fontSize:'14px', color:'#0077b6', fontWeight:600, padding:'8px 0 14px' },
  reportHeader: { display:'flex', padding:'8px 12px', background:'#e0f0ff', fontSize:'12px', color:'#0077b6', fontWeight:600, borderRadius:'6px', marginBottom:'4px' },
  reportRow: { display:'flex', padding:'8px 12px', borderBottom:'1px solid #f0f7ff', background:'#fff' },
  reportLabel: { flex:2, fontSize:'13px', color:'#333' },
  reportLast: { flex:1, textAlign:'right', fontSize:'13px', color:'#888' },
  reportThis: { flex:1, textAlign:'right', fontSize:'13px', color:'#0077b6', fontWeight:600 },
  reportUsage: { flex:1, textAlign:'right', fontSize:'13px', fontWeight:700 },
};
